import { Tab, Tabs } from '@material-ui/core';
import { PageTitleGoBack } from 'components/base/title/PageTitle';
import TabPanel from 'components/common/tab/TabPanel';
import ProjectChatContainer from 'containers/project/ProjectChatContainer';
import ProjectFileContainer from 'containers/project/ProjectFileContainer';
import ProjectDetailContainer from 'containers/project/ProjectDetailContainer';
import { a11yProps } from 'lib/library';
import { IRemake } from 'lib/types/projectTypes';
import moment from 'moment';
import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import StageTimeline from './StageTimeline';
import SyncMessage from './SyncMessage';
import { color } from 'styles/utils';
import cx from 'classnames';
import { lowerCase } from 'lodash';
import ExcuteApps from './ExcuteApps';
import PlainModal from 'components/common/modal/PlainModal';
import AppModal from 'components/common/modal/AppModal';
import GoldStatusModalContainer from 'containers/project/GoldStatusModalContainer';

interface ProjectProps {
  hasOwnTimeline: any;
  isOwner: boolean;
  isShowChat: boolean;
  goldVisible: boolean;
  goldStatusModalOpen: boolean;
  language: string;
  hasAlarm: any;
  indicationFormat: object | any;
  indicationInfo: object | any;
  projectInfo?: object | any;
  isProjectCreator: boolean;
  managerList: object | any;
  remakeList: IRemake[];
  prevPartnerManager: number | null;
  partnerManager: {
    value: number;
    onChange: (e: any) => void;
    setValue: (value: number) => void;
  };
  nowProjectTab: number;
  projectTabList: any[];
  onOpenCompleteProjectModal: () => void;
  onChangePartnerManager: (number: number) => void;
  onEditProjectSync: (syncStatus: number) => void;
  onCloseGoldStatusModal: () => void;
  onClickGoToBack: () => void;
  onClickChat: () => void;
  onClickProjectTab: (tab: number) => void;
}

function Project({
  hasOwnTimeline,
  isOwner,
  isShowChat,
  goldVisible,
  goldStatusModalOpen,
  language,
  hasAlarm,
  indicationFormat,
  indicationInfo,
  projectInfo,
  isProjectCreator,
  managerList,
  remakeList,
  prevPartnerManager,
  partnerManager,
  nowProjectTab,
  projectTabList,
  onOpenCompleteProjectModal,
  onChangePartnerManager,
  onEditProjectSync,
  onCloseGoldStatusModal,
  onClickGoToBack,
  onClickChat,
  onClickProjectTab,
}: ProjectProps) {
  const { t } = useTranslation();
  const {
    stage,
    projectCode,
    clientGroupCode,
    timeline,
    projectName,
    ownerGroupCode,
    partnerStage,
    partnerTimeline,
    clientPartnershipCode,
    clientIdx,
    partnershipCode,
    ownerIdx,
    enrollDate,
    ownerName,
    partnerName,
  } = projectInfo;

  useEffect(() => {
    console.log('PMS] nowProjectTab _________ ', nowProjectTab);
  }, [nowProjectTab]);

  return (
    <StyledProject data-component-name="Project">
      {useMemo(
        () => (
          <PlainModal isOpen={goldStatusModalOpen} onClose={onCloseGoldStatusModal} width={1000}>
            <AppModal
              title={t('GLOBAL_GOLD_HOLD')}
              headerCenterText={''}
              headerCenterTextStyle={{ width: 325 }}
              content={
                <GoldStatusModalContainer
                  partnershipIdx={isProjectCreator ? clientIdx : ownerIdx}
                  partnershipCode={isProjectCreator ? clientPartnershipCode : partnershipCode}
                  partnerGroupCode={isProjectCreator ? clientGroupCode : ownerGroupCode}
                  partnerGroupIdx={isProjectCreator ? clientIdx : ownerIdx}
                  onClose={onCloseGoldStatusModal}
                />
              }
              contentCardStyle={{
                padding: '30px 35px 25px',
                borderRadius: '8px',
              }}
              isCloseIcon={true}
              onCancel={onCloseGoldStatusModal}
              hideButton={true}
            />
          </PlainModal>
        ),
        [goldStatusModalOpen],
      )}
      <PageTitleGoBack
        title={projectName}
        goBack={onClickGoToBack}
        date={moment
          .unix(enrollDate)
          .locale(language)
          .format(language === 'en' ? 'MMM. DD. YYYY' : 'YYYY. MM. DD')}
        timelineLegends={{
          owner: !!isOwner && !!ownerName ? ownerName : 'Client',
          partner: !isOwner && !!partnerName ? partnerName : 'Partner',
        }}
        useChat={true}
        useGoldState={!hasAlarm && !!goldVisible && !!clientGroupCode}
        isShowChat={!hasAlarm && isShowChat && !!clientGroupCode}
        onClickChat={onClickChat}
        onClickGold={onCloseGoldStatusModal}
      />

      <div className="project__info_top_item">
        {hasAlarm && (
          <div className="top_item syncMessage">
            <SyncMessage projectInfo={projectInfo} onEditProjectSync={onEditProjectSync} />
          </div>
        )}
        {hasOwnTimeline && (
          <div className="top_item_box">
            <div className="timeline">
              <StageTimeline
                projectInfo={projectInfo}
                ownerGroupCode={ownerGroupCode}
                stage={stage}
                timeline={timeline}
                partnerStage={partnerStage}
                partnerTimeline={partnerTimeline}
                projectCode={projectCode}
                onOpenCompleteProjectModal={onOpenCompleteProjectModal}
              />
            </div>
            <div className="excuteApp">
              <ExcuteApps />
            </div>
          </div>
        )}
      </div>

      <div className="project__info_container">
        <div className="project__tabs_box">
          <Tabs
            value={nowProjectTab}
            onChange={(e: any, value: any) => {
              onClickProjectTab(value);
            }}
            aria-label="myproduct tabs"
            className="project__tabs"
          >
            {projectTabList.map((item, idx) => (
              <Tab
                className={cx('project__tab_item')}
                key={idx}
                label={item}
                disableRipple
                {...a11yProps(idx, 'myProjectTab')}
              />
            ))}
          </Tabs>
          <div className="tabs_empty_margin"></div>

          <TabPanel
            value={nowProjectTab}
            index={0}
            name="myProjectTab"
            className="project__tab_tabpanel"
          >
            <ProjectDetailContainer
              indicationFormat={indicationFormat}
              indicationInfo={indicationInfo}
              language={language}
              hasAlarm={hasAlarm}
              projectInfo={projectInfo}
              isProjectCreator={isProjectCreator}
              managerList={managerList}
              remakeList={remakeList}
              prevPartnerManager={prevPartnerManager}
              partnerManager={partnerManager}
              onChangePartnerManager={onChangePartnerManager}
            />
          </TabPanel>
          <TabPanel
            value={nowProjectTab}
            index={1}
            name="myProjectTab"
            className="project__tab_tabpanel"
          >
            <ProjectFileContainer hasViewer={true} />
          </TabPanel>
        </div>
      </div>
      <ProjectChatContainer isShowChat={isShowChat} onClickChat={onClickChat} />
    </StyledProject>
  );
}

export default React.memo(Project);

const StyledProject = styled.div`
  position: relative;
  .project__info_top_item {
    position: relative;
    margin-bottom: 20px;
    /* height: 96px; */
    /* height: 128px; */
    .top_item_box {
      display: flex;
      justify-content: space-between;
      height: 87px;
      .timeline {
        width: 834px;
        background-color: #fff;
        border: 1px solid #e1e1e7;
        border-radius: 10px;
      }
      .excuteApp {
        max-width: 740px;
        /* width: 100%; */
        background-color: #fff;
        border: 1px solid #e1e1e7;
        border-radius: 10px;
      }
    }
  }
  .project__info_container {
    margin: 0 auto;
    width: 100%;

    .project__tabs_box {
      position: relative;
      width: inherit;
      background-color: #fff;
      border: 1px solid ${color.border_line};
      box-shadow: 4px 4px 10px 0px rgba(0, 0, 0, 0.16);
      border-radius: 10px;
      .project__tabs {
        width: inherit;
        border-radius: 10px 10px 0 0;

        .MuiTabs-indicator {
          display: none;
        }
        .project__tab_item {
          width: 200px;
          min-height: 52px;
          text-transform: capitalize;
          border-right: none;
          background-color: ${color.back_ground_bg};
          border-bottom: 1px solid ${color.border_line};

          &:nth-child(1) {
            &.Mui-selected {
              border-right: 1px solid ${color.border_line};
            }
          }

          &:nth-child(2) {
            &.Mui-selected {
              border-left: 1px solid ${color.border_line};
            }
          }

          &.Mui-selected {
            background-color: #fff;
            border-bottom: none;
            color: #00a6e2;
          }
        }
      }
      .tabs_empty_margin {
        position: absolute;
        top: 0;
        right: 0;
        height: 52px;
        border-left: 1px solid ${color.border_line};
        border-bottom: 1px solid ${color.border_line};
        /* display: flex;
        justify-content: flex-end; */
        width: calc(100% - 400px);
        z-index: 1;
      }
    }
  }
`;
